import { useState } from 'react';
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
} from 'recharts';
import {
  useRunSimulation,
  useSimulationResults,
  useSimulationHistory,
  type SimulationType,
  type SimulationConfig,
  type SimulationResult,
} from '../api/simulation';

const SIM_TYPES: { value: SimulationType; label: string; description: string }[] = [
  { value: 'monte_carlo', label: 'Monte Carlo', description: 'Probabilistic risk simulation with random sampling' },
  { value: 'what_if', label: 'What-If', description: 'Scenario analysis with configurable parameters' },
  { value: 'forecast', label: 'Forecast', description: 'Time-series prediction with confidence intervals' },
];

const MONTE_CARLO_DEFAULTS = { iterations: 10000, confidence_level: 0.95, seed: '' };
const WHAT_IF_DEFAULTS = { scenario_name: '', risk_factor_change: 0, entity_removal: '' };
const FORECAST_DEFAULTS = { time_horizon_days: 90, metric: 'risk_score', model: 'arima' };

export default function SimulationPage() {
  const [simType, setSimType] = useState<SimulationType>('monte_carlo');
  const [simName, setSimName] = useState('');
  const [monteCarloParams, setMonteCarloParams] = useState(MONTE_CARLO_DEFAULTS);
  const [whatIfParams, setWhatIfParams] = useState(WHAT_IF_DEFAULTS);
  const [forecastParams, setForecastParams] = useState(FORECAST_DEFAULTS);
  const [selectedResultId, setSelectedResultId] = useState<string | null>(null);

  const runSimulation = useRunSimulation();
  const { data: selectedResult } = useSimulationResults(selectedResultId);
  const { data: history } = useSimulationHistory({ page: 1, page_size: 20 });

  function handleRun() {
    let parameters: Record<string, unknown> = {};
    if (simType === 'monte_carlo') parameters = { ...monteCarloParams };
    else if (simType === 'what_if') parameters = { ...whatIfParams };
    else parameters = { ...forecastParams };

    const config: SimulationConfig = {
      type: simType,
      name: simName || `${simType} - ${new Date().toLocaleString()}`,
      parameters,
      iterations: simType === 'monte_carlo' ? monteCarloParams.iterations : undefined,
      time_horizon_days: simType === 'forecast' ? forecastParams.time_horizon_days : undefined,
    };

    runSimulation.mutate(config, {
      onSuccess: (result) => setSelectedResultId(result.id),
    });
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-100">Simulation Engine</h1>
        <p className="text-sm text-gray-400 mt-1">Run Monte Carlo, What-If, and Forecast simulations</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Configuration Panel */}
        <div className="xl:col-span-1 space-y-4">
          {/* Simulation Type */}
          <div className="bg-surface-secondary rounded-xl border border-border-subtle p-5">
            <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-4">
              Simulation Type
            </h2>
            <div className="space-y-2">
              {SIM_TYPES.map((t) => (
                <button
                  key={t.value}
                  onClick={() => setSimType(t.value)}
                  className={`w-full text-left p-3 rounded-lg border transition-all ${
                    simType === t.value
                      ? 'bg-helios-700/30 border-cyber-cyan/40 text-gray-100'
                      : 'bg-surface-tertiary border-border-subtle text-gray-400 hover:text-gray-200 hover:border-gray-600'
                  }`}
                >
                  <div className="font-medium text-sm">{t.label}</div>
                  <div className="text-xs mt-0.5 opacity-70">{t.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Parameters */}
          <div className="bg-surface-secondary rounded-xl border border-border-subtle p-5">
            <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-4">
              Parameters
            </h2>

            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Simulation Name</label>
                <input
                  type="text"
                  value={simName}
                  onChange={(e) => setSimName(e.target.value)}
                  placeholder="Optional name..."
                  className="w-full bg-surface-tertiary border border-border-subtle rounded-lg px-3 py-2 text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-cyber-cyan/50"
                />
              </div>

              {simType === 'monte_carlo' && (
                <>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Iterations</label>
                    <input
                      type="number"
                      value={monteCarloParams.iterations}
                      onChange={(e) =>
                        setMonteCarloParams((p) => ({ ...p, iterations: parseInt(e.target.value) || 1000 }))
                      }
                      className="w-full bg-surface-tertiary border border-border-subtle rounded-lg px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyber-cyan/50"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Confidence Level</label>
                    <select
                      value={monteCarloParams.confidence_level}
                      onChange={(e) =>
                        setMonteCarloParams((p) => ({ ...p, confidence_level: parseFloat(e.target.value) }))
                      }
                      className="w-full bg-surface-tertiary border border-border-subtle rounded-lg px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyber-cyan/50"
                    >
                      <option value={0.9}>90%</option>
                      <option value={0.95}>95%</option>
                      <option value={0.99}>99%</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Random Seed (optional)</label>
                    <input
                      type="text"
                      value={monteCarloParams.seed}
                      onChange={(e) => setMonteCarloParams((p) => ({ ...p, seed: e.target.value }))}
                      placeholder="For reproducibility"
                      className="w-full bg-surface-tertiary border border-border-subtle rounded-lg px-3 py-2 text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-cyber-cyan/50"
                    />
                  </div>
                </>
              )}

              {simType === 'what_if' && (
                <>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Scenario Name</label>
                    <input
                      type="text"
                      value={whatIfParams.scenario_name}
                      onChange={(e) => setWhatIfParams((p) => ({ ...p, scenario_name: e.target.value }))}
                      placeholder="e.g. Remove key node"
                      className="w-full bg-surface-tertiary border border-border-subtle rounded-lg px-3 py-2 text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-cyber-cyan/50"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Risk Factor Change (%)</label>
                    <input
                      type="number"
                      value={whatIfParams.risk_factor_change}
                      onChange={(e) =>
                        setWhatIfParams((p) => ({ ...p, risk_factor_change: parseFloat(e.target.value) || 0 }))
                      }
                      className="w-full bg-surface-tertiary border border-border-subtle rounded-lg px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyber-cyan/50"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Entity ID to Remove (optional)</label>
                    <input
                      type="text"
                      value={whatIfParams.entity_removal}
                      onChange={(e) => setWhatIfParams((p) => ({ ...p, entity_removal: e.target.value }))}
                      placeholder="Entity ID..."
                      className="w-full bg-surface-tertiary border border-border-subtle rounded-lg px-3 py-2 text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-cyber-cyan/50"
                    />
                  </div>
                </>
              )}

              {simType === 'forecast' && (
                <>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Time Horizon (days)</label>
                    <input
                      type="number"
                      value={forecastParams.time_horizon_days}
                      onChange={(e) =>
                        setForecastParams((p) => ({ ...p, time_horizon_days: parseInt(e.target.value) || 30 }))
                      }
                      className="w-full bg-surface-tertiary border border-border-subtle rounded-lg px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyber-cyan/50"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Metric</label>
                    <select
                      value={forecastParams.metric}
                      onChange={(e) => setForecastParams((p) => ({ ...p, metric: e.target.value }))}
                      className="w-full bg-surface-tertiary border border-border-subtle rounded-lg px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyber-cyan/50"
                    >
                      <option value="risk_score">Risk Score</option>
                      <option value="alert_count">Alert Count</option>
                      <option value="entity_growth">Entity Growth</option>
                      <option value="transaction_volume">Transaction Volume</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Model</label>
                    <select
                      value={forecastParams.model}
                      onChange={(e) => setForecastParams((p) => ({ ...p, model: e.target.value }))}
                      className="w-full bg-surface-tertiary border border-border-subtle rounded-lg px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyber-cyan/50"
                    >
                      <option value="arima">ARIMA</option>
                      <option value="prophet">Prophet</option>
                      <option value="lstm">LSTM</option>
                    </select>
                  </div>
                </>
              )}
            </div>

            <button
              onClick={handleRun}
              disabled={runSimulation.isPending}
              className="w-full mt-4 px-4 py-2.5 rounded-lg bg-gradient-to-r from-cyber-cyan to-helios-600 text-white font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {runSimulation.isPending ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Running...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.347a1.125 1.125 0 01-1.667-.986V5.653z" />
                  </svg>
                  Run Simulation
                </>
              )}
            </button>

            {runSimulation.isError && (
              <p className="mt-2 text-xs text-red-400">
                Error: {(runSimulation.error as Error).message}
              </p>
            )}
          </div>
        </div>

        {/* Results Panel */}
        <div className="xl:col-span-2 space-y-4">
          {/* Active Result */}
          {selectedResult && <SimulationResultPanel result={selectedResult} />}

          {/* No result selected */}
          {!selectedResult && !runSimulation.isPending && (
            <div className="bg-surface-secondary rounded-xl border border-border-subtle p-12 flex flex-col items-center justify-center text-center">
              <svg className="w-16 h-16 text-gray-600 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5" />
              </svg>
              <h3 className="text-lg font-medium text-gray-300">No Simulation Running</h3>
              <p className="text-sm text-gray-500 mt-1">Configure parameters and run a simulation to see results</p>
            </div>
          )}

          {/* History */}
          <div className="bg-surface-secondary rounded-xl border border-border-subtle p-5">
            <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-4">
              History
            </h2>
            {history && history.items.length > 0 ? (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {history.items.map((sim) => (
                  <button
                    key={sim.id}
                    onClick={() => setSelectedResultId(sim.id)}
                    className={`w-full text-left p-3 rounded-lg border transition-all ${
                      selectedResultId === sim.id
                        ? 'bg-helios-700/30 border-cyber-cyan/40'
                        : 'bg-surface-tertiary border-border-subtle hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-200">{sim.name}</span>
                      <StatusBadge status={sim.status} />
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                      <span className="capitalize">{sim.type.replace('_', ' ')}</span>
                      <span>{new Date(sim.created_at).toLocaleString()}</span>
                      {sim.duration_ms && <span>{(sim.duration_ms / 1000).toFixed(1)}s</span>}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No simulations yet</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    running: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    completed: 'bg-green-500/20 text-green-400 border-green-500/30',
    failed: 'bg-red-500/20 text-red-400 border-red-500/30',
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full border ${colors[status] || colors.pending}`}>
      {status}
    </span>
  );
}

function SimulationResultPanel({ result }: { result: SimulationResult }) {
  if (result.status === 'running' || result.status === 'pending') {
    return (
      <div className="bg-surface-secondary rounded-xl border border-border-subtle p-8 flex flex-col items-center justify-center">
        <svg className="animate-spin w-10 h-10 text-cyber-cyan mb-4" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        <h3 className="text-lg font-medium text-gray-300">Simulation Running...</h3>
        <p className="text-sm text-gray-500 mt-1">{result.name}</p>
      </div>
    );
  }

  if (result.status === 'failed') {
    return (
      <div className="bg-surface-secondary rounded-xl border border-red-500/30 p-6">
        <h3 className="text-lg font-medium text-red-400">Simulation Failed</h3>
        <p className="text-sm text-gray-400 mt-1">{result.error || 'Unknown error'}</p>
      </div>
    );
  }

  const results = result.results;
  if (!results) return null;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-surface-secondary rounded-xl border border-border-subtle p-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-100">{result.name}</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {result.type.replace('_', ' ').toUpperCase()} &middot; Completed{' '}
              {result.completed_at ? new Date(result.completed_at).toLocaleString() : ''} &middot;{' '}
              {result.duration_ms ? `${(result.duration_ms / 1000).toFixed(1)}s` : ''}
            </p>
          </div>
          <StatusBadge status={result.status} />
        </div>
      </div>

      {/* Confidence Intervals */}
      {results.confidence_intervals && results.confidence_intervals.length > 0 && (
        <div className="bg-surface-secondary rounded-xl border border-border-subtle p-5">
          <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-4">
            Confidence Intervals
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {results.confidence_intervals.map((ci, i) => (
              <div key={i} className="bg-surface-tertiary rounded-lg p-3 border border-border-subtle">
                <div className="text-xs text-gray-500">{ci.label}</div>
                <div className="text-xl font-bold text-gray-100 mt-1">{ci.mean.toFixed(2)}</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  [{ci.lower.toFixed(2)} - {ci.upper.toFixed(2)}]
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Risk Distribution Chart */}
      {results.risk_distribution && results.risk_distribution.length > 0 && (
        <div className="bg-surface-secondary rounded-xl border border-border-subtle p-5">
          <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-4">
            Risk Distribution
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={results.risk_distribution}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="bucket" tick={{ fill: '#94a3b8', fontSize: 12 }} />
              <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} />
              <Tooltip
                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: 8 }}
                labelStyle={{ color: '#e2e8f0' }}
              />
              <Bar dataKey="count" fill="#06b6d4" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Timeline / Forecast Chart */}
      {results.timeline && results.timeline.length > 0 && (
        <div className="bg-surface-secondary rounded-xl border border-border-subtle p-5">
          <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-4">
            Forecast Timeline
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={results.timeline}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 12 }} />
              <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} />
              <Tooltip
                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: 8 }}
                labelStyle={{ color: '#e2e8f0' }}
              />
              {results.timeline[0]?.lower !== undefined && (
                <Area type="monotone" dataKey="upper" stroke="none" fill="#06b6d4" fillOpacity={0.1} />
              )}
              {results.timeline[0]?.lower !== undefined && (
                <Area type="monotone" dataKey="lower" stroke="none" fill="#06b6d4" fillOpacity={0.1} />
              )}
              <Line type="monotone" dataKey="value" stroke="#06b6d4" strokeWidth={2} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Data Points Table */}
      {results.data_points && results.data_points.length > 0 && (
        <div className="bg-surface-secondary rounded-xl border border-border-subtle p-5">
          <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-4">
            Data Points
          </h3>
          <div className="overflow-x-auto max-h-64 overflow-y-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-subtle">
                  {Object.keys(results.data_points[0]).map((key) => (
                    <th key={key} className="text-left py-2 px-3 text-xs font-medium text-gray-400 uppercase">
                      {key}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {results.data_points.slice(0, 50).map((row, i) => (
                  <tr key={i} className="border-b border-border-subtle/50 hover:bg-surface-tertiary">
                    {Object.values(row).map((val, j) => (
                      <td key={j} className="py-2 px-3 text-gray-300">
                        {typeof val === 'number' ? val.toFixed(4) : String(val)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
